import axios from "axios";

/**
 * Pull a user-facing message out of whatever the catch block received.
 *
 * Centralised so individual components don't have to spell out the
 *`error?.response?.data?.message ?? error.message ??"fallback"`* defensively-chained pattern (which is also where most uses of`any`* crept in across the client).
 *
 * Handles three shapes:
 * 1. Real`AxiosError`instances (production runtime).
 * 2. Plain objects shaped like`{ response: { data: { message } } }`* — useful for tests / mocks and for libraries that copy the axios
 * contract without inheriting from`AxiosError`.
 * 3. Native`Error`instances and bare strings (anything thrown).
 */

interface AxiosLikeError {
 response?: {
 status?: unknown;
 data?: unknown;
 };
 message?: unknown;
}

const asAxiosLike = (err: unknown): AxiosLikeError | null => {

 if (axios.isAxiosError(err)) {
 return err as AxiosLikeError;
 }

 if (err && typeof err ==="object"&&"response"in err) {
 return err as AxiosLikeError;
 }

 return null;
};

export const extractErrorMessage = (
 err: unknown,
 fallback ="Something went wrong"): string => {

 const axiosLike = asAxiosLike(err);

 if (axiosLike) {
 const data = axiosLike.response?.data;

 if (data && typeof data ==="object"&&"message"in data) {
 const m = (data as { message?: unknown }).message;
 if (typeof m ==="string"&& m.length > 0) return m;
 }

 if (typeof axiosLike.message ==="string"&& axiosLike.message.length > 0) {
 return axiosLike.message;
 }
 }

 if (err instanceof Error && err.message) return err.message;

 if (typeof err ==="string"&& err.length > 0) return err;

 return fallback;
};

/**
 * Pull the HTTP status code out of an axios-like error, or`null`if the
 * value is not a recognisable axios error (e.g. a thrown Error from our
 * own code).
 */
export const extractStatus = (err: unknown): number | null => {

 const axiosLike = asAxiosLike(err);

 if (!axiosLike) return null;

 const status = axiosLike.response?.status;

 return typeof status ==="number"? status : null;
};
